import { notify } from '@shared/toaster/toast'
import { HttpError } from '@shared/api'
import type { LoginResponse } from './types'

const DEFAULT_MESSAGES = {
  success: 'Signed in successfully.',
  requiresOtp: 'Enter the OTP to continue.',
  invalidOtp: 'Invalid OTP. Please try again.',
  failed: 'Login failed.',
  turnstileRequired: 'Please complete human verification.',
  userNameRequired: 'Enter your user ID.',
  passwordRequired: 'Enter your password.',
  otpRequired: 'Enter the OTP.',
  sessionExpired: 'Login attempt expired. Please sign in again.',
} as const

export function notifyLoginOutcome(result: LoginResponse): void {
  const message = result.message?.trim()

  switch (result.outcome) {
    case 'Success':
      notify.success(message || DEFAULT_MESSAGES.success)
      break
    case 'RequiresOtp':
      notify.info(message || DEFAULT_MESSAGES.requiresOtp)
      break
    case 'InvalidOtp':
      notify.error(message || DEFAULT_MESSAGES.invalidOtp)
      break
    case 'Failed':
    default:
      notify.error(message || DEFAULT_MESSAGES.failed)
      break
  }
}

export function notifyLoginRequestError(error: unknown): void {
  if (isAbortError(error)) {
    return
  }

  if (error instanceof HttpError) {
    if (error.status === 502) {
      notify.error(
        'API is not running. Start Http.Api (dotnet run --project Http.Api --launch-profile http), then try again.',
      )
      return
    }

    notify.error(error.message || DEFAULT_MESSAGES.failed)
    return
  }

  const message = error instanceof Error ? error.message : String(error)
  if (isNetworkUnreachable(message)) {
    notify.error(
      'Cannot reach the API. Start Http.Api on http://127.0.0.1:5188, then try again.',
    )
    return
  }

  notify.error(message || DEFAULT_MESSAGES.failed)
}

export const loginNotify = {
  turnstileRequired: () => notify.warning(DEFAULT_MESSAGES.turnstileRequired),
  userNameRequired: () => notify.warning(DEFAULT_MESSAGES.userNameRequired),
  passwordRequired: () => notify.warning(DEFAULT_MESSAGES.passwordRequired),
  otpRequired: () => notify.warning(DEFAULT_MESSAGES.otpRequired),
  sessionExpired: () => notify.warning(DEFAULT_MESSAGES.sessionExpired),
  turnstileExpired: () =>
    notify.warning('Verification expired. Please complete it again.'),
  turnstileUnavailable: () =>
    notify.error('Could not load human verification. Refresh the page and try again.'),
}

function isAbortError(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === 'AbortError'
}

function isNetworkUnreachable(message: string): boolean {
  return (
    message === 'Network Error' ||
    message === 'Failed to fetch' ||
    message.includes('NetworkError') ||
    message.includes('ECONNREFUSED')
  )
}
